/**
 * Interval and Frequency Helper Functions
 * Centralized logic for converting maintenance job intervals to days
 */

/**
 * Convert interval and unit to days
 * @param {number} interval - The interval number (e.g., 1, 2, 7)
 * @param {string} unit - The unit type ('day', 'week', 'month', etc.)
 * @returns {number} Number of days
 */
export function convertIntervalToDays(interval, unit) {
  if (!interval || !unit) return 0;

  const normalizedUnit = unit.toLowerCase();

  switch (normalizedUnit) {
    case 'day':
    case 'days':
      return interval;
    case 'week':
    case 'weeks':
      return interval * 7;
    case 'month':
    case 'months':
      return interval * 30;
    case 'year':
    case 'years':
      return interval * 365;
    default:

      return 0;
  }
}

/**
 * Get the frequency in days from a job object
 * Handles multiple frequency field formats (interval+unit, frequencyMonths, frequency)
 * @param {object} job - The job object
 * @returns {number} Frequency in days
 */
export function getJobFrequencyDays(job) {
  if (!job) return 365; // Default to yearly

  // Priority 1: Check for interval + unit (newer format for daily/weekly/bi-weekly)
  if (job.interval && job.unit) {
    return convertIntervalToDays(job.interval, job.unit);
  }

  // Priority 2: Check for frequencyMonths (legacy format for monthly jobs)
  if (job.frequencyMonths) {
    return job.frequencyMonths * 30;
  }

  // Priority 3: Check for frequency field (fallback)
  if (job.frequency) {
    return job.frequency * 30;
  }

  // Default: yearly
  return 365;
}

/**
 * Get last completed date for a job from maintenance history
 * Handles both array format [{jobId, completedAt}] and object format {jobId: date}
 * @param {object} job - The job object (must have id property)
 * @param {Array|object} maintenanceHistory - Maintenance history in either format
 * @returns {string|null} The last completed date as ISO string, or null if never completed
 */
export function getJobLastCompleted(job, maintenanceHistory) {
  if (!maintenanceHistory || !job?.id) return null;

  // Handle array format: [{jobId: "xyz", completedAt: "2025-01-01"}, ...]
  if (Array.isArray(maintenanceHistory)) {
    const record = maintenanceHistory.find(r => r.jobId === job.id);
    return record?.completedAt || record?.date || null;
  }

  // Handle object format: {jobId: "2025-01-01", ...} or {jobId: {completedAt: "2025-01-01"}, ...}
  const entry = maintenanceHistory[job.id];
  if (!entry) return null;
  if (typeof entry === 'string') return entry;
  return entry?.completedAt || entry?.date || null;
}

/**
 * Get the "due soon" threshold in days based on job frequency
 * - Annual jobs (180+ days): 30-day warning
 * - All other jobs (weekly, monthly, quarterly): 7-day warning
 * @param {object} job - The job object with frequency fields
 * @param {number|null} customDays - Optional custom threshold from user settings (advanceWarningDays)
 * @returns {number} Number of days before due date to show "due soon" status
 */
export function getDueSoonThreshold(job, customDays = null) {
  // If user has set custom advance warning days in settings, use that
  if (customDays !== null && customDays > 0) {
    return customDays;
  }

  const frequencyDays = getJobFrequencyDays(job);

  // Annual jobs (180+ days): 30-day warning
  if (frequencyDays >= 180) return 30;

  // All other jobs (monthly, weekly, quarterly): 7-day warning
  return 7;
}